<template>
  <tbody v-if="article.count > 0">
    <tr>
      <td rowspan="2" class="hidden-xs">
        <img v-if="hasImages" :src="'/images/articles/' + article.images[0].path" height="50px">
        <img v-else :src="ARTICLE_DEFAULT_IMAGE" height="50px">
      </td>
      <td>
        <div v-if="unitIsSqm"> {{ article.displayName }} {{ article.count }}m²</div>
        <div v-if="unitIsPc">{{ article.count }}x {{ article.displayName }}</div>
        <small v-show="hasModifiedCount">
          ({{ countDiff }} {{ (article.inclusive_unit == 'pc') ? $t('labels.per_unit') : '/&#13217;' }} {{ $t('labels.inclusive_abbreviation')}})
        </small><br>
        {{ $filters.formatMoney(price) }} / {{ (unitIsPc) ? $t('labels.per_unit') : '&#13217;' }}
      </td>
      <td class="text-right">{{ $filters.formatMoney(totalNet) }}</td>
      <td v-if="!netValuesOnly" class="text-right">{{ $filters.formatMoney(totalVat) }}</td>
      <td v-if="!netValuesOnly" class="text-right">{{ $filters.formatMoney(total) }}</td>
    </tr>

    <tr v-if="!isPrint">
      <td :colspan="(!netValuesOnly) ? 4 : 2">
        <div class="input-group input-group-sm col-xs-4 col-sm-2">
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              @click="removeFromBasket(article)"
              :disabled="article.count <= article.inclusive">
              <i class="fa fa-minus" aria-hidden="true"></i>
            </button>
          </span>
          <input
            class="form-control"
            type="number"
            :step="(unitIsPc) ? 1 : 0.01"
            :min="article.inclusive"
            :value="article.count"
            name="depth"
            @blur="updateCount(article, $event.target)">
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              @click="addToBasket(article)">
              <i class="fa fa-plus" aria-hidden="true"></i>
            </button>
          </span>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script setup>
import { ARTICLE_DEFAULT_IMAGE, UNIT_PC, UNIT_SQM } from '@/constants'
import { useMainStore } from '@/store'
import { addLateDeliverySurcharge } from '@/utils/helper'
import { computed } from 'vue'

const store = useMainStore()
// define passed props
const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
  isPrint: {
    type: Boolean,
    default: false,
  },
	netValuesOnly: {
		type: Boolean,
		default: false,
	},
})
// define emits
const emit = defineEmits(['remove', 'updateCount', 'add'])
// define computed props
const locale = computed(() => store.settings.locale)
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)
const shoppingCart = computed(() => store.settings.shopping_cart)

const price = computed(() => {
  return (earlyBirdMode.value)
    ? addLateDeliverySurcharge(props.article.exhibitionPrices.price_net, lateDelivery.value)
    : props.article.exhibitionPrices.price_net
})

const displayName = computed(() => {
  return (locale.value === 'en') ? props.article.name_en : props.article.name
})

const unitIsPc = computed(() => {
  return props.article.unit === UNIT_PC
})

const unitIsSqm = computed(() => {
  return props.article.unit === UNIT_SQM
})

const hasImages = computed(() => {
  return props.article.images !== null
    && props.article.images.length > 0
    && props.article.images[0].path !== null
})

const hasModifiedCount = computed(() => {
  if (!props.article.hasOwnProperty('count_after_inclusive')) {
    return false
  }

  return props.article.count !== props.article.count_after_inclusive
})

const countDiff = computed(() => {
  if (hasModifiedCount.value) {
    return props.article.count - props.article.count_after_inclusive
  }

  return 0
})

const totalNet = computed(() => {
  let count = 0
  if (unitIsSqm.value) {
    //calculate inclusive sqm with current booth size
    let boothInclusive = props.article.inclusive * shoppingCart.value.getSquareMeter()
    count = ((props.article.count - props.article.inclusive) > 0)
      ? (props.article.count - boothInclusive)
      : 0
  } else {
    count = ((props.article.count - props.article.inclusive) > 0)
      ? (props.article.count - props.article.inclusive)
      : 0
  }

  if (hasModifiedCount.value) {
    count = props.article.count_after_inclusive
  }

  return count * parseFloat(price.value)
})

const total = computed(() => {
  return totalNet.value * (1 + (parseFloat(props.article.exhibitionPrices.price_vat_rate) / 100) )
})

const totalVat = computed(() => {
  return total.value - totalNet.value
})
// define methods
const removeFromBasket = (() => {
  emit('remove', props.article)
})

const updateCount = ((target) => {
  emit('updateCount', props.article, target)
})

const addToBasket = (() => {
  emit('add', props.article)
})
</script>